/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

/**
 * segnalaErrori.js — invio degli errori runtime del frontend a `POST /api/errori`.
 *
 * Oggi un errore che fa cadere una schermata si scopre solo se un operatore lo
 * racconta. In campo, durante una ricerca, non lo racconta nessuno: si rinuncia
 * alla funzione e si tira avanti. Questo modulo li fa arrivare da soli.
 *
 * Quattro scelte che sembrano sbagliate e non lo sono:
 *
 * 1. **`fetch` diretto, non l'istanza axios dell'app.** Se sono l'interceptor o
 *    il livello offline a essere la causa del guasto, segnalare attraverso di
 *    essi non funziona. Per la stessa ragione NON si usa la coda offline
 *    (`offlineQueue.js`, IndexedDB), che è codice complesso e quindi essa stessa
 *    un possibile imputato: la riserva qui è un `localStorage` scritto a mano.
 * 2. **Una segnalazione per impronta per sessione.** Senza deduplica, un errore
 *    che si ripete manda migliaia di richieste — cioè il primo bug diventa un
 *    attacco involontario al proprio server.
 * 3. **L'impronta locale serve SOLO a deduplicare.** Quella vera la calcola il
 *    server e non viene mai inviata: è ciò che tiene insieme l'aggregazione, e
 *    un client che la sbagliasse creerebbe righe scollegate.
 * 4. **Non fallisce mai.** Tutto dentro `try`, e in caso di dubbio si tace. Un
 *    modulo che segnala errori non può esserne la causa.
 *
 * ⚠️ Tranello che costa tempo: `fetch` NON rifiuta sulle risposte 4xx/5xx. Senza
 * controllare `risposta.ok` la riserva non scatterebbe mai, perché ogni invio
 * sembrerebbe riuscito. Vedi `inviaAdesso()`.
 */

const CHIAVE_RISERVA = 'tas:errori:riserva';
const MAX_RISERVA = 20;          // oltre, si buttano le più vecchie: non è un archivio
const MAX_MESSAGGIO = 2000;
const MAX_STACK = 6000;

// Impronte già inviate in QUESTA sessione. In memoria di proposito: a ogni
// ricaricamento si riparte, così un guasto che dura nel tempo continua a
// rinfrescare `ultimo_il` sul server senza però inondarlo mentre la pagina vive.
const gia_inviate = new Set();

const urlApi = () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/errori`;

const tronca = (v, n) => (typeof v === 'string' ? v.slice(0, n) : '');

/**
 * Percorso corrente SENZA query string.
 *
 * Il taglio si fa anche qui, oltre che sul server: la query può contenere il
 * token di reset password o l'id di sessione, e il modo più sicuro di non
 * mandarli è non metterli mai nel corpo della richiesta.
 */
const percorsoCorrente = () => {
  try {
    return `${window.location.pathname || ''}${window.location.hash ? window.location.hash.split('?')[0] : ''}`;
  } catch (_) {
    return '';
  }
};

/** Impronta locale, solo per deduplicare in sessione. Quella buona la fa il server. */
const improntaLocale = (messaggio, percorso, stack) => {
  const primaRiga = String(stack || '').split('\n').map((r) => r.trim()).find(Boolean) || '';
  return `${messaggio}|${percorso}|${primaRiga}`;
};

const leggiRiserva = () => {
  try {
    const grezzo = localStorage.getItem(CHIAVE_RISERVA);
    const lista = grezzo ? JSON.parse(grezzo) : [];
    return Array.isArray(lista) ? lista : [];
  } catch (_) {
    return [];
  }
};

const scriviRiserva = (lista) => {
  try {
    localStorage.setItem(CHIAVE_RISERVA, JSON.stringify(lista.slice(-MAX_RISERVA)));
  } catch (_) {
    // localStorage pieno o disabilitato: si perde la riserva, non l'app.
  }
};

const metttiInRiserva = (corpo) => {
  const lista = leggiRiserva();
  lista.push(corpo);
  scriviRiserva(lista);
};

/**
 * Invio singolo. Solleva se la risposta non è ok, così il chiamante sa che deve
 * mettere in riserva — vedi il tranello di `fetch` nell'intestazione.
 */
const inviaAdesso = async (corpo) => {
  const risposta = await fetch(urlApi(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Il token, se c'è, serve solo ad attribuire la riga: la rotta è pubblica
      // perché un errore di render capita anche prima del login.
      ...(() => {
        try {
          const t = localStorage.getItem('tas_token');
          return t ? { Authorization: `Bearer ${t}` } : {};
        } catch (_) {
          return {};
        }
      })(),
    },
    body: JSON.stringify(corpo),
    keepalive: true,   // la richiesta sopravvive se la pagina viene chiusa subito dopo
  });
  if (!risposta.ok) throw new Error(`HTTP ${risposta.status}`);
};

/**
 * Segnala un errore. Non solleva mai.
 *
 * @param {Error|string} errore
 * @param {'render'|'finestra'|'promessa'} contesto
 */
export const segnalaErrore = async (errore, contesto = 'render') => {
  try {
    const messaggio = tronca(
      (errore && errore.message) ? String(errore.message) : String(errore || 'Errore sconosciuto'),
      MAX_MESSAGGIO,
    );
    if (!messaggio.trim()) return;

    const stack = tronca(errore && errore.stack ? String(errore.stack) : '', MAX_STACK);
    const percorso = percorsoCorrente();

    const chiave = improntaLocale(messaggio, percorso, stack);
    if (gia_inviate.has(chiave)) return;
    gia_inviate.add(chiave);

    const corpo = {
      messaggio,
      stack: stack || null,
      percorso,
      contesto,
      versione: process.env.REACT_APP_VERSION || '',
    };

    try {
      await inviaAdesso(corpo);
    } catch (_) {
      // Rete assente, server giù, 4xx/5xx: si mette da parte e si riprova al
      // prossimo avvio. In campo la rete va e viene, ed è proprio quando manca
      // che gli errori interessanti succedono.
      metttiInRiserva(corpo);
    }
  } catch (_) {
    // Nemmeno il fallimento della segnalazione deve arrivare all'app.
  }
};

/** Svuota la riserva. Chiamata all'avvio; ogni invio riuscito toglie la voce. */
export const inviaRiserva = async () => {
  try {
    const lista = leggiRiserva();
    if (!lista.length) return;
    scriviRiserva([]);                 // si svuota PRIMA, così un fallimento non duplica
    const rimaste = [];
    for (const corpo of lista) {
      try {
        await inviaAdesso(corpo);
      } catch (_) {
        rimaste.push(corpo);
      }
    }
    if (rimaste.length) scriviRiserva(rimaste);
  } catch (_) {
    /* silenzio */
  }
};

/**
 * Aggancia i gestori globali: errori fuori dal ciclo di rendering, che nessun
 * ErrorBoundary vedrebbe (callback, timer, promesse non gestite).
 *
 * Idempotente: chiamarla due volte non raddoppia le segnalazioni.
 */
let agganciati = false;
export const agganciaGestoriGlobali = () => {
  if (agganciati) return;
  agganciati = true;
  try {
    window.addEventListener('error', (ev) => {
      segnalaErrore(ev && ev.error ? ev.error : (ev && ev.message) || 'window.onerror', 'finestra');
    });
    window.addEventListener('unhandledrejection', (ev) => {
      const motivo = ev && ev.reason;
      segnalaErrore(motivo instanceof Error ? motivo : String(motivo), 'promessa');
    });
    inviaRiserva();
  } catch (_) {
    /* silenzio */
  }
};

// Esportata solo per i test: azzera la deduplica di sessione.
export const _azzeraDeduplica = () => gia_inviate.clear();
